<template>
  <div class="activity-logs">
    <content-placeholder v-if="loading">
      <el-card>
        <p class="text text-10 text-h-20"></p>
        <p class="text text-50"></p>
        <p class="text text-25"></p>
        <p class="text text-25"></p>
      </el-card>
    </content-placeholder>

    <el-card v-else v-loading="reloading">
      <div slot="header">User Logs</div>  
      <div v-if="loadError">
        <error-block :buttonCallback="fetchData" />
      </div>

      <div v-else>
        <el-table 
          :data="logs"
          :stripe="true"
          @filter-change="handleFilterChange"
          @sort-change="handleSortChange"
          :border="true"
          class="depth">
          <el-table-column
            column-key="author_id_filter"
            prop="author"
            label="Author"
            :filters="authorFilters"
            v-if="!userId">
            <template slot-scope="scope">
              <span v-if="scope.row.author.profile">{{ scope.row.author.profile.first_name }} {{ scope.row.author.profile.last_name }}</span>
              <span v-else>{{ scope.row.author.email }}</span>
            </template>
          </el-table-column>
          <el-table-column
            column-key="action_filter"
            prop="action"
            label="Action"
            :filters="actionFilters"
            :formatter="actionFormatter">
          </el-table-column>
          <el-table-column
            prop="ip_address"
            label="IP Address"
            align="center">
          </el-table-column>
          <el-table-column
            prop="device_name"
            label="Device Name"
            align="center">
          </el-table-column>
          <el-table-column
            prop="browser_name"
            label="Browser Name"
            align="center">
          </el-table-column>
          <el-table-column
            prop="created_at"
            label="Created at"
            sortable
            :formatter="dateFormatter">
          </el-table-column>
          <el-table-column
            width="120"
            align="center"
            v-if="this.showLink">
            <template slot-scope="scope">
              <router-link :to="{ name: 'userProfile', params: { id: scope.row.author.id } }">
                <el-button type="primary" size="mini">
                  <i class="fas fa-search"></i>
                </el-button>
              </router-link>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            @current-change="handleCurrentChange"
            :current-page.sync="currentPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="total">
          </el-pagination>
        </div>

      </div>
    </el-card>
  </div>
</template>

<script>
  import { map, isEmpty, forEach, includes } from 'lodash';
  import { mapActions, mapGetters } from 'vuex';

  import { LOG_ACTIONS } from '@/store/modules/log/constants';
  import { formatTime } from '@/filters/time';

  export default {
    name: 'ActivityLogs',
    props: {
      userId: {
        type: Number,
      },
      showLink: {
        type: Boolean,
        default: true,
      },
    },
    created() {
      this.fetchData();
      if (!this.userId) {
        this.fetchUsers({ scope: 'employee' });
      }
    },
    data() {
      return {
        loading: true,
        reloading: false,
        loadError: null,
        currentPage: 1,
        pageSize: 10,
        total: 0,
        sortColumn: 'created_at',
        sortOrder: 'desc',
        filters: {},
      };
    },
    computed: {
      ...mapGetters('entities/logs', ['query']),

      authorFilters() {
        const users = this.$store.getters['entities/users/query']()
          .with('profile')
          .where('admin', false)
          .get();

        if (!this.userId) {
          return map(users, (user) => {
            const text = (user.profile) ? `${user.email} (${user.profile.first_name} ${user.profile.last_name})` : user.email;

            return {
              text,
              value: user.id,
            };
          });
        }

        return [];
      },

      actionFilters() {
        return map(LOG_ACTIONS, index => ({
          text: this.$t(`modules.log.actions.${index}`),
          value: index,
        }));
      },

      logs() {
        const baseQuery = this.query()
                .with('author.profile')
                .orderBy(this.sortColumn, this.sortOrder)
                .offset((this.currentPage - 1) * this.pageSize)
                .limit(this.pageSize);

        forEach(this.filters, (filterOptions, field) => {
          if (!isEmpty(filterOptions)) {
            baseQuery.where(field.replace('_filter', ''), value => includes(filterOptions, parseInt(value, 10)));
          }
        });

        if (this.userId) {
          baseQuery.where('author_id', this.userId);
        }

        return baseQuery.get();
      },
    },
    methods: {
      ...mapActions('entities/logs', ['fetchLogs']),
      ...mapActions('entities/users', ['fetchUsers']),

      buildParams() {
        const params = {
          page: this.currentPage,
          per_page: this.pageSize,
          sort_field: this.sortColumn,
          sort_order: this.sortOrder,
          ...this.filters,
        };

        if (this.userId) params.user_id = this.userId;

        return params;
      },

      fetchData() {
        this.showLoader();
        this.loadError = null;
        this.fetchLogs(this.buildParams())
          .then((response) => {
            if (response.status) {
              this.total = response.data.meta.total;
            } else {
              this.loadError = response.error;
            }

            this.hideLoader();
          });
      },

      actionFormatter(row, column) {
        const property = column.property;
        return this.$t(`modules.log.actions.${row[property]}`);
      },

      dateFormatter(row, column) {
        const property = column.property;
        return formatTime(row[property], 'MMMM DD, YYYY hh:mm a');
      },

      handleCurrentChange(val) {
        this.currentPage = val;
        this.fetchData();
      },

      handleSortChange({ prop, order }) {
        this.sortColumn = prop;
        this.sortOrder = order === 'descending' ? 'desc' : 'asc';
        this.fetchData();
      },

      handleFilterChange(filters) {
        this.filters = filters;
        this.fetchData();
      },

      showLoader() {
        if (this.logs.length) {
          this.reloading = true;
        } else {
          this.loading = true;
        }
      },

      hideLoader() {
        this.reloading = false;
        this.loading = false;
      },
    },
  };
</script>

<style lang="scss">
  .activity-logs {
    .pagination {
      margin-top: 20px;
      text-align: center;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/activityLog/ActivityLogs.vue